import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Download,
  Printer,
  Globe,
  Package,
  HeartHandshake,
  Search,
  Share2,
  Smartphone,
  Megaphone,
  CheckCircle2,
  XCircle,
  MinusCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Target,
  Lightbulb,
  FileBarChart,
  Sparkles,
  Info,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  Legend,
} from "recharts";
import { cn, getScoreColor, getScoreLabel, formatDate, SECTION_COLORS } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import QUESTION_TIPS from "@/lib/questionTips";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

const SECTION_ICON_MAP = {
  discover: Globe,
  deliver: Package,
  delight: HeartHandshake,
  dominate: Search,
  discourse: Share2,
  mobile: Smartphone,
  drive: Megaphone,
};

const SECTION_META = {
  discover: { title: "Website Presence & Product Experience", color: "#FACC15" },
  mobile: { title: "Mobile Optimization", color: "#F97316" },
  drive: { title: "Marketing Readiness", color: "#A855F7" },
  deliver: { title: "Post-Purchase & Packaging", color: "#22C55E" },
  delight: { title: "Customer Support", color: "#EC4899" },
  dominate: { title: "Search Visibility (SEO)", color: "#3B82F6" },
  discourse: { title: "Social Footprint", color: "#8B5CF6" },
};

const RECOMMENDATIONS = {
  discover: [
    "Implement a comprehensive site search with autocomplete and filters",
    "Add trust badges and security seals prominently in the header",
    "Optimize product images with zoom functionality and multiple angles",
    "Streamline checkout to reduce cart abandonment",
    "Add customer reviews and ratings on product pages",
  ],
  deliver: [
    "Implement real-time order tracking with SMS/WhatsApp updates",
    "Create branded unboxing experience with personalized thank you notes",
    "Add delivery status notifications at each stage",
    "Include clear return labels and instructions in packages",
    "Survey customers post-delivery for continuous improvement",
  ],
  delight: [
    "Implement live chat with <30 second response time",
    "Create comprehensive self-service FAQ and knowledge base",
    "Add WhatsApp Business for instant customer support",
    "Implement ticket management system for tracking issues",
    "Set up automated follow-up after support interactions",
  ],
  dominate: [
    "Conduct comprehensive keyword research and on-page optimization",
    "Implement schema markup for products, reviews, and organization",
    "Improve page speed with image optimization and caching",
    "Build quality backlinks through content marketing",
    "Set up and optimize Google Business Profile",
  ],
  discourse: [
    "Create consistent posting schedule (3-5 posts per week)",
    "Leverage user-generated content and customer testimonials",
    "Set up Instagram Shopping for seamless product discovery",
    "Engage with community through comments and direct messages",
    "Partner with micro-influencers for authentic reach",
  ],
  drive: [
    "Install Meta and Google tracking pixels on all pages",
    "Set up GA4 with proper conversion event tracking",
    "Connect product catalog to Meta and Google Shopping",
    "Configure Google Search Console for indexing insights",
    "Track key funnel events: Add to Cart, Checkout, Purchase",
  ],
  mobile: [
    "Ensure all banners and images adapt properly for mobile screens",
    "Implement sticky Add to Cart button on mobile product pages",
    "Optimize mobile checkout with appropriate keyboards and autofill",
    "Enable mobile payment options (Apple Pay, Google Pay)",
    "Test site speed on mobile - aim for under 3s on 4G",
    "Consider Progressive Web App (PWA) for app-like experience",
  ],
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="text-white font-medium">{label}</p>
        <p className="text-primary">{`Score: ${payload[0].value}%`}</p>
      </div>
    );
  }
  return null;
};

export default function Report() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const reportRef = useRef(null);
  const [audit, setAudit] = useState(null);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    if (!authLoading) fetchData();
  }, [auditId, authLoading, isAuthenticated]);

  const fetchData = async () => {
    try {
      const [auditRes, sectionsRes] = await Promise.all([
        axios.get(`${API}/audits/${auditId}`),
        axios.get(`${API}/sections`),
      ]);
      setAudit(auditRes.data);
      setSections(sectionsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load report");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`IMPRESS-360-Audit-${audit.client_info.store_name}-${formatDate(audit.created_at)}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  if (!audit) return null;

  // Prepare chart data
  const barChartData = Object.entries(audit.sections).map(([id, data]) => ({
    name: data.section_name,
    score: Math.round(data.score),
    fill: SECTION_META[id]?.color || "#FACC15",
  }));

  const pieChartData = [
    { name: "Answered", value: audit.answered_questions, fill: "#22C55E" },
    { name: "Unanswered", value: audit.total_questions - audit.answered_questions, fill: "#27272A" },
  ];

  const radialData = [
    {
      name: "Score",
      value: Math.round(audit.overall_score),
      fill: audit.overall_score >= 75 ? "#22C55E" : audit.overall_score >= 50 ? "#FACC15" : "#EF4444",
    },
  ];

  // Get issues (questions with "no" answers)
  const getIssues = () => {
    const issues = [];
    Object.entries(audit.sections).forEach(([sectionId, sectionData]) => {
      const sectionDef = sections[sectionId];
      if (!sectionDef) return;
      
      sectionData.answers.forEach((answer) => {
        if (answer.answer === "no") {
          // Find question text
          let questionText = "";
          sectionDef.subsections.forEach((sub) => {
            const q = sub.questions.find((q) => q.id === answer.question_id);
            if (q) questionText = q.text;
          });
          if (questionText) {
            issues.push({
              section: sectionData.section_name,
              sectionId,
              question: questionText,
              notes: answer.notes,
            });
          }
        }
      });
    });
    return issues;
  };

  const issues = getIssues();
  
  // Sort sections by score for strengths/weaknesses
  const sortedSections = Object.entries(audit.sections)
    .map(([id, data]) => ({ id, ...data }))
    .sort((a, b) => b.score - a.score);

  const strengths = sortedSections.filter((s) => s.score >= 70).slice(0, 2);
  const weaknesses = sortedSections.filter((s) => s.score < 70).slice(-2).reverse();

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header - Non-printable */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/dashboard/${auditId}`)}
                className="text-zinc-400 hover:text-white"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="heading-sm text-white">Audit Report</h1>
                <p className="text-sm text-zinc-500">{audit.client_info.store_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="print-report-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                onClick={exportToPDF}
                disabled={exporting}
                className="btn-primary"
                data-testid="export-pdf-btn"
              >
                {exporting ? (
                  <div className="spinner w-4 h-4" />
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Report Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div ref={reportRef} className="space-y-8 bg-zinc-950 p-4 rounded-xl">
          {/* Report Header */}
          <div className="text-center pb-6 border-b border-zinc-800">
            <img src={LOGO_URL} alt="Digital Impressions" className="h-12 w-auto mx-auto mb-4" />
            <h1 className="heading-xl text-white mb-2">IMPRESS 360 AUDIT REPORT</h1>
            <p className="text-zinc-400">{audit.client_info.store_name} • {formatDate(audit.client_info.audit_date)}</p>
          </div>

          {/* Executive Summary */}
          <Card className="card-glass" data-testid="executive-summary">
            <CardHeader>
              <CardTitle className="heading-md text-white flex items-center gap-2">
                <Target className="w-6 h-6 text-primary" />
                Executive Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Overall Score */}
                <div className="flex flex-col items-center justify-center p-6 bg-zinc-900/50 rounded-xl">
                  <ResponsiveContainer width={150} height={150}>
                    <RadialBarChart
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="100%"
                      data={radialData}
                      startAngle={90}
                      endAngle={-270}
                    >
                      <RadialBar
                        dataKey="value"
                        cornerRadius={10}
                        background={{ fill: "#27272A" }}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <div className="text-center -mt-20">
                    <span className={cn("text-4xl font-bold", getScoreColor(audit.overall_score))}>
                      {Math.round(audit.overall_score)}
                    </span>
                    <span className="text-zinc-500">/100</span>
                  </div>
                  <Badge className={cn(
                    "mt-6",
                    audit.overall_score >= 75 ? "score-badge-high" :
                    audit.overall_score >= 50 ? "score-badge-medium" : "score-badge-low"
                  )}>
                    {getScoreLabel(audit.overall_score)}
                  </Badge>
                </div>

                {/* Key Stats */}
                <div className="space-y-4">
                  <div className="p-4 bg-zinc-900/50 rounded-xl">
                    <p className="text-sm text-zinc-500 mb-1">Questions Answered</p>
                    <p className="text-2xl font-bold text-white">
                      {audit.answered_questions} / {audit.total_questions}
                    </p>
                  </div>
                  <div className="p-4 bg-zinc-900/50 rounded-xl">
                    <p className="text-sm text-zinc-500 mb-1">Issues Identified</p>
                    <p className="text-2xl font-bold text-red-400">{issues.length}</p>
                  </div>
                  <div className="p-4 bg-zinc-900/50 rounded-xl">
                    <p className="text-sm text-zinc-500 mb-1">Audited By</p>
                    <p className="text-lg font-medium text-white">{audit.client_info.auditor_name}</p>
                  </div>
                </div>

                {/* Completion Pie */}
                <div className="flex flex-col items-center justify-center">
                  <ResponsiveContainer width="100%" height={150}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={60}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-zinc-400 mt-2">Audit Completion</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section Scores Chart */}
          <Card className="card-glass" data-testid="section-scores-chart">
            <CardHeader>
              <CardTitle className="heading-md text-white">Section Scores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis type="number" domain={[0, 100]} stroke="#71717A" />
                    <YAxis dataKey="name" type="category" width={100} stroke="#71717A" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                      {barChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Strengths & Weaknesses */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Strengths */}
            <Card className="card-glass border-green-500/20" data-testid="strengths-card">
              <CardHeader>
                <CardTitle className="heading-md text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Key Strengths
                </CardTitle>
              </CardHeader>
              <CardContent>
                {strengths.length > 0 ? (
                  <div className="space-y-4">
                    {strengths.map((section) => {
                      const Icon = SECTION_ICON_MAP[section.id];
                      return (
                        <div key={section.id} className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
                          <Icon className="w-5 h-5 text-green-500" />
                          <div className="flex-1">
                            <p className="text-white font-medium">{section.section_name}</p>
                            <p className="text-sm text-zinc-400">{SECTION_META[section.id]?.title}</p>
                          </div>
                          <span className="text-green-500 font-bold">{Math.round(section.score)}%</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-zinc-500">Complete more sections to identify strengths</p>
                )}
              </CardContent>
            </Card>

            {/* Weaknesses */}
            <Card className="card-glass border-red-500/20" data-testid="weaknesses-card">
              <CardHeader>
                <CardTitle className="heading-md text-white flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  Areas for Improvement
                </CardTitle>
              </CardHeader>
              <CardContent>
                {weaknesses.length > 0 ? (
                  <div className="space-y-4">
                    {weaknesses.map((section) => {
                      const Icon = SECTION_ICON_MAP[section.id];
                      return (
                        <div key={section.id} className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg">
                          <Icon className="w-5 h-5 text-red-500" />
                          <div className="flex-1">
                            <p className="text-white font-medium">{section.section_name}</p>
                            <p className="text-sm text-zinc-400">{SECTION_META[section.id]?.title}</p>
                          </div>
                          <span className="text-red-500 font-bold">{Math.round(section.score)}%</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-zinc-500">Great job! All sections are performing well</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Issues Identified */}
          {issues.length > 0 && (
            <Card className="card-glass" data-testid="issues-card">
              <CardHeader>
                <CardTitle className="heading-md text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Issues Identified ({issues.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] custom-scrollbar">
                  <div className="space-y-3">
                    {issues.map((issue, index) => (
                      <div
                        key={index}
                        className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800"
                      >
                        <div className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <Badge 
                              variant="outline" 
                              className="mb-2"
                              style={{ 
                                borderColor: SECTION_META[issue.sectionId]?.color,
                                color: SECTION_META[issue.sectionId]?.color 
                              }}
                            >
                              {issue.section}
                            </Badge>
                            <p className="text-white">{issue.question}</p>
                            {issue.notes && (
                              <p className="text-sm text-zinc-400 mt-2 italic">Note: {issue.notes}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          <Card className="card-glass" data-testid="recommendations-card">
            <CardHeader>
              <CardTitle className="heading-md text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={weaknesses[0]?.id || "discover"} className="w-full">
                <TabsList className="bg-zinc-900 border border-zinc-800 mb-4">
                  {Object.entries(SECTION_META).map(([id, meta]) => {
                    const Icon = SECTION_ICON_MAP[id];
                    return (
                      <TabsTrigger
                        key={id}
                        value={id}
                        className="data-[state=active]:bg-zinc-800"
                        data-testid={`tab-${id}`}
                      >
                        <Icon className="w-4 h-4 mr-2" style={{ color: meta.color }} />
                        <span className="hidden sm:inline">{id.charAt(0).toUpperCase() + id.slice(1)}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                
                {Object.entries(RECOMMENDATIONS).map(([sectionId, recs]) => (
                  <TabsContent key={sectionId} value={sectionId}>
                    <div className="space-y-3">
                      {recs.map((rec, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-3 bg-zinc-900/30 rounded-lg"
                        >
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <p className="text-zinc-300">{rec}</p>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Section Details */}
          <Card className="card-glass" data-testid="section-details-card">
            <CardHeader>
              <CardTitle className="heading-md text-white">Section Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(audit.sections).map(([sectionId, sectionData]) => {
                  const Icon = SECTION_ICON_MAP[sectionId];
                  const meta = SECTION_META[sectionId];
                  const yesCount = sectionData.answers.filter(a => a.answer === "yes").length;
                  const partialCount = sectionData.answers.filter(a => a.answer === "partial").length;
                  const noCount = sectionData.answers.filter(a => a.answer === "no").length;
                  
                  return (
                    <div
                      key={sectionId}
                      className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${meta.color}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: meta.color }} />
                        </div>
                        <div>
                          <h3 className="font-bold text-white">{sectionData.section_name}</h3>
                          <p className={cn("text-sm font-medium", getScoreColor(sectionData.score))}>
                            {Math.round(sectionData.score)}% Score
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span className="text-zinc-400">{yesCount}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MinusCircle className="w-4 h-4 text-yellow-500" />
                          <span className="text-zinc-400">{partialCount}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span className="text-zinc-400">{noCount}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Question & Answer Report by Section */}
          <Card className="card-glass print-break" data-testid="detailed-qa-card">
            <CardHeader>
              <CardTitle className="heading-md text-white flex items-center gap-2">
                <FileBarChart className="w-5 h-5 text-primary" />
                Detailed Audit Responses
              </CardTitle>
              <p className="text-sm text-zinc-400 mt-1">Complete question-by-question breakdown by section</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {Object.entries(audit.sections).map(([sectionId, sectionData]) => {
                  const Icon = SECTION_ICON_MAP[sectionId];
                  const meta = SECTION_META[sectionId];
                  const sectionDef = sections[sectionId];
                  
                  if (!sectionDef) return null;
                  
                  return (
                    <div key={sectionId} className="border border-zinc-800 rounded-xl overflow-hidden">
                      {/* Section Header */}
                      <div 
                        className="p-4 flex items-center gap-3"
                        style={{ backgroundColor: `${meta?.color}15` }}
                      >
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${meta?.color}30` }}
                        >
                          {Icon && <Icon className="w-5 h-5" style={{ color: meta?.color }} />}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-white text-lg">{sectionData.section_name}</h3>
                          <p className="text-sm text-zinc-400">{meta?.title}</p>
                        </div>
                        <div className="text-right">
                          <span className={cn("text-2xl font-bold", getScoreColor(sectionData.score))}>
                            {Math.round(sectionData.score)}%
                          </span>
                          <p className="text-xs text-zinc-500">Section Score</p>
                        </div>
                      </div>
                      
                      {/* Questions by Subsection */}
                      <div className="p-4 space-y-6">
                        {sectionDef.subsections?.map((subsection, subIdx) => (
                          <div key={subIdx}>
                            <h4 className="text-sm font-semibold text-zinc-300 mb-3 pb-2 border-b border-zinc-800">
                              {subsection.name}
                            </h4>
                            <div className="space-y-2">
                              {subsection.questions?.map((question, qIdx) => {
                                const answer = sectionData.answers?.find(a => a.question_id === question.id);
                                const answerValue = answer?.answer || "unanswered";
                                const notes = answer?.notes;
                                
                                const getAnswerDisplay = (val) => {
                                  switch(val) {
                                    case "yes": return { text: "Yes", color: "text-green-400", bg: "bg-green-500/20" };
                                    case "no": return { text: "No", color: "text-red-400", bg: "bg-red-500/20" };
                                    case "partial": return { text: "Partial", color: "text-yellow-400", bg: "bg-yellow-500/20" };
                                    case "na": return { text: "N/A", color: "text-zinc-400", bg: "bg-zinc-500/20" };
                                    default: return { text: "—", color: "text-zinc-600", bg: "bg-zinc-800" };
                                  }
                                };
                                
                                const display = getAnswerDisplay(answerValue);
                                const tip = QUESTION_TIPS[question.id];
                                const showSuggestion = (answerValue === "no" || answerValue === "partial") && tip;
                                
                                return (
                                  <div key={question.id} className="py-3 border-b border-zinc-800/30 last:border-0">
                                    <div className="flex items-start gap-3">
                                      <span className="flex-shrink-0 w-6 h-6 rounded bg-zinc-800 flex items-center justify-center text-xs text-zinc-500">
                                        {qIdx + 1}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-zinc-300">{question.text}</p>
                                        {notes && (
                                          <p className="text-xs text-zinc-500 mt-1 italic">Note: {notes}</p>
                                        )}
                                        {showSuggestion && (
                                          <div className="mt-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                                            <div className="flex items-start gap-2">
                                              <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                              <div className="space-y-2">
                                                <div>
                                                  <span className="text-xs font-semibold text-primary">Suggestion: </span>
                                                  <span className="text-xs text-zinc-400">{tip.howTo}</span>
                                                </div>
                                                <div>
                                                  <span className="text-xs font-semibold text-green-400">Benefit: </span>
                                                  <span className="text-xs text-zinc-400">{tip.benefit}</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <span className={cn(
                                        "flex-shrink-0 px-3 py-1 rounded text-xs font-medium",
                                        display.bg, display.color
                                      )}>
                                        {display.text}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recommendations Summary */}
          <Card className="card-glass" data-testid="recommendations-summary-card">
            <CardHeader>
              <CardTitle className="heading-md text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                Key Recommendations Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {weaknesses.slice(0, 3).map((section) => {
                  const recs = RECOMMENDATIONS[section.id] || [];
                  const Icon = SECTION_ICON_MAP[section.id];
                  const meta = SECTION_META[section.id];
                  
                  return (
                    <div key={section.id} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                      <div className="flex items-center gap-2 mb-3">
                        {Icon && <Icon className="w-5 h-5" style={{ color: meta?.color }} />}
                        <h4 className="font-semibold text-white">{section.section_name}</h4>
                        <Badge variant="outline" className="ml-auto border-red-500/30 text-red-400">
                          Priority Focus
                        </Badge>
                      </div>
                      <ul className="space-y-2">
                        {recs.slice(0, 3).map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Closing Statement */}
          <Card className="card-glass glow-yellow-sm" data-testid="closing-statement-card">
            <CardContent className="p-8 text-center">
              <img src={LOGO_URL} alt="Digital Impressions" className="h-14 w-auto mx-auto mb-6" />
              <h2 className="heading-lg text-white mb-4">Ready to Transform Your Digital Presence?</h2>
              <p className="text-zinc-300 text-lg mb-6 max-w-2xl mx-auto">
                This audit is just the beginning. At <span className="text-primary font-semibold">Digital Impressions</span>, 
                we don't just identify opportunities — we help you seize them. Let's take your brand to the next level 
                and create digital experiences that truly <span className="text-primary font-semibold">IMPRESS</span>.
              </p>
              <div className="flex items-center justify-center gap-2 text-primary font-bold text-xl mb-4">
                <Sparkles className="w-6 h-6" />
                <span>Your Success is Our Impression</span>
                <Sparkles className="w-6 h-6" />
              </div>
              <p className="text-zinc-500 text-sm">
                Contact us at <span className="text-zinc-300">thedigitalimpressions.com</span> to discuss your audit results
              </p>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center pt-6 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm">
              Generated by IMPRESS 360 Audit • Digital Impressions • {formatDate(new Date())}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
